import type { SpeechRecognitionPort } from '@elara/client-core/lib/speech-recognition-port';

/**
 * Web `SpeechRecognitionPort` implementation backed by the Web Speech API (ELARA-754).
 *
 * Wraps `window.SpeechRecognition` / `webkitSpeechRecognition` and translates its DOM events into
 * the port's neutral callbacks: each `onresult` is split into the interim transcript (reported every
 * event) and any final transcript (committed). The DOM type declarations for the Web Speech API live
 * here — they are web-only and must not leak into the shared store or `@elara/mobile`.
 *
 * A missing global (SSR, hardened contexts, browsers without the API) reports `isSupported: false`
 * and `start()` returns `undefined`, so callers degrade gracefully rather than throwing.
 */

// Minimal DOM type declarations for the Web Speech API (not in the standard lib typings).
interface WebSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((event: WebSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

interface WebSpeechRecognitionEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface WebSpeechRecognitionErrorEvent extends Event {
  error: string;
}

declare global {
  interface Window {
    SpeechRecognition: new () => WebSpeechRecognition;
    webkitSpeechRecognition: new () => WebSpeechRecognition;
  }
}

export function createWebSpeechRecognitionPort(): SpeechRecognitionPort {
  const isSupported =
    typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  return {
    isSupported,
    start(callbacks) {
      if (!isSupported) return undefined;

      const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new Ctor();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        callbacks.onStart?.();
      };

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result && result[0]) {
            const transcript = result[0].transcript;
            if (result.isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }
        }
        // Report the interim preview on every event; commit any final text after.
        callbacks.onInterimTranscript(interimTranscript);
        if (finalTranscript) {
          callbacks.onFinalTranscript(finalTranscript);
        }
      };

      recognition.onerror = (event) => {
        callbacks.onError(event.error);
      };

      recognition.onend = () => {
        callbacks.onEnd();
      };

      recognition.start();
      return {
        stop() {
          recognition.stop();
        },
      };
    },
  };
}
